
import React, { useRef, useEffect } from 'react';
import { googleAuthService } from '@/auth/google';
import { useAuth } from '@/auth/useAuth';

interface GoogleSignInButtonProps {
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  className?: string;
  customText?: string;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  text = 'signin_with',
  theme = 'outline',
  size = 'large',
  className = '',
  customText,
}) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { isLoading, isLoggingIn } = useAuth();

  useEffect(() => {
    if (!buttonRef.current || isLoading || isLoggingIn) return;

    const renderButton = async () => {
      try {
        // Ensure Google is initialized before rendering
        await googleAuthService.initialize();
        
        if (buttonRef.current) {
          // Clear the button container first
          buttonRef.current.innerHTML = '';
          
          if (customText) {
            // Create a custom styled button that looks like Google's button
            const customButton = document.createElement('button');
            customButton.className = 'flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500';
            customButton.innerHTML = `
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              ${customText}
            `;
            buttonRef.current.appendChild(customButton);
          } else {
            googleAuthService.renderButton(buttonRef.current, {
              type: 'standard',
              theme,
              size,
              text,
              shape: 'rectangular',
            });
          }
        }
      } catch (error) {
        console.error('Failed to render Google button:', error);
      }
    };

    renderButton();
  }, [isLoading, isLoggingIn, theme, size, text, customText]);

  if (isLoading) {
    return (
      <div className={`h-12 bg-gray-100 animate-pulse rounded border flex items-center justify-center ${className}`}>
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={buttonRef} 
        className={`flex justify-center ${isLoggingIn ? 'opacity-50 pointer-events-none' : ''}`}
        style={{ minWidth: customText ? 'auto' : '240px' }}
      />
      {isLoggingIn && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
        </div>
      )}
    </div>
  );
};
